import React, { useState, useEffect } from "react";
import { X, Loader, Save } from "lucide-react";
import {
  AppServiceDependency,
  ServiceAssetWithSelection,
  listServiceAssetsWithSelection,
  saveServiceAssetSelections,
} from "../../../../api/frameworksApi";

interface ServiceAssetSelectorModalProps {
  applicationId: string;
  applicationName: string;
  dep: AppServiceDependency;
  onClose: () => void;
  onSaved: (serviceId: string, items: ServiceAssetWithSelection[]) => void;
}

export function ServiceAssetSelectorModal({
  applicationId,
  applicationName,
  dep,
  onClose,
  onSaved,
}: ServiceAssetSelectorModalProps) {
  const [items, setItems] = useState<ServiceAssetWithSelection[]>([]);
  const [checked, setChecked] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    listServiceAssetsWithSelection(applicationId, dep.serviceId)
      .then((res) => {
        if (cancelled) return;
        const data = res.data ?? [];
        setItems(data);
        const initial: Record<string, boolean> = {};
        data.forEach((i) => { initial[i.asset.id] = i.isSelected; });
        setChecked(initial);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load assets.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [applicationId, dep.serviceId]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const selectedIds = items.map((i) => i.asset.id).filter((id) => checked[id]);
      const res = await saveServiceAssetSelections(applicationId, dep.serviceId, selectedIds);
      onSaved(dep.serviceId, res.data ?? []);
      onClose();
    } catch {
      setError("Failed to save selection.");
    } finally {
      setSaving(false);
    }
  }

  const allChecked = items.length > 0 && items.every((i) => checked[i.asset.id]);

  function toggleAll() {
    const next = !allChecked;
    const updated: Record<string, boolean> = {};
    items.forEach((i) => { updated[i.asset.id] = next; });
    setChecked(updated);
  }

  return (
    <div className="fixed inset-0 z-[100] flex justify-center overflow-y-auto bg-black/30 backdrop-blur-sm p-4">
      <div className="bg-background border border-border rounded-xl shadow-xl w-full max-w-lg my-auto flex flex-col max-h-[760px]">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 p-5 border-b border-border">
          <div>
            <h2 className="text-base font-semibold text-foreground">
              Assets for "{dep.service.name}"
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Select which assets apply to "{applicationName}"
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-muted-foreground hover:bg-muted transition-colors"
          >
            <X className="size-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground gap-2 text-sm">
              <Loader className="size-4 animate-spin" />
              Loading assets…
            </div>
          ) : items.length === 0 ? (
            <p className="text-center py-12 text-sm text-muted-foreground">
              No assets registered on this service.
            </p>
          ) : (
            <div className="space-y-1.5">
              {/* Select-all row */}
              <label className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-muted cursor-pointer border border-transparent hover:border-border transition-colors">
                <input
                  type="checkbox"
                  checked={allChecked}
                  onChange={toggleAll}
                  className="size-4 accent-indigo-600 cursor-pointer"
                />
                <span className="text-xs font-semibold text-muted-foreground tracking-wider">
                  Select all
                </span>
              </label>
              <div className="border-t border-border my-1" />
              {items.map((item) => (
                <label
                  key={item.asset.id}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted cursor-pointer border border-transparent hover:border-border transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={!!checked[item.asset.id]}
                    onChange={(e) =>
                      setChecked((prev) => ({ ...prev, [item.asset.id]: e.target.checked }))
                    }
                    className="size-4 accent-indigo-600 cursor-pointer"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">{item.asset.ciName}</p>
                    <p className="text-xs text-muted-foreground">{item.asset.ciIdentifier ?? "—"}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
                      {item.asset.role}
                    </span>
                    <span className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400">
                      {item.asset.status}
                    </span>
                  </div>
                </label>
              ))}
            </div>
          )}

          {error && (
            <p className="mt-3 text-xs text-red-600 dark:text-red-400">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 p-4 border-t border-border">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? <Loader className="size-4 animate-spin" /> : <Save className="size-4" />}
            Save Selection
          </button>
        </div>
      </div>
    </div>
  );
}
